import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  structuredData?: object;
  noindex?: boolean;
  children?: React.ReactNode;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Maa Saryu Agency - Premium Tiles & Sanitary Solutions in Ayodhya",
  description = "Leading supplier of premium tiles, granite, CP fittings, bath fittings, and sanitary ware in Ayodhya. Wholesale & retail solutions for homes and businesses. 15+ years experience.",
  keywords = [
    "tiles ayodhya",
    "granite ayodhya", 
    "sanitary ware ayodhya",
    "cp fittings ayodhya",
    "bath fittings ayodhya",
    "maa saryu agency",
    "bathroom tiles",
    "floor tiles",
    "wall tiles",
    "ceramic tiles",
    "porcelain tiles",
    "marble tiles",
    "granite slabs",
    "kitchen fittings",
    "bath accessories",
    "wholesale tiles",
    "retail tiles",
    "tile supplier uttar pradesh"
  ],
  canonicalUrl,
  ogImage = "/logo512.jpg",
  ogType = "website",
  ogTitle,
  ogDescription,
  structuredData,
  noindex = false,
  children
}) => {
  const siteUrl = "https://maasaryuagency.com"; // Replace with your actual domain
  const fullCanonicalUrl = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content="Maa Saryu Agency" />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="Maa Saryu Agency" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#1a237e" />
      <meta name="msapplication-TileColor" content="#1a237e" />
      
      {/* Geo Location */}
      <meta name="geo.region" content="IN-UP" />
      <meta name="geo.placename" content="Ayodhya" />
      <meta name="geo.position" content="26.7922;82.1998" />
      <meta name="ICBM" content="26.7922, 82.1998" />

      {/* Business Information */}
      <meta name="contact" content="info@maasaryuagency.com" />
      <meta name="coverage" content="Ayodhya, Uttar Pradesh, India" />
      <meta name="distribution" content="local" />
      <meta name="rating" content="general" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional children elements */}
      {children}
    </Helmet>
  );
};

export default SEOHead;
